/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, forwardRef, useEffect, MutableRefObject } from 'react'
import * as Sentry from '@sentry/nextjs'
import { Video, Transformation } from 'cloudinary-react'

import { Scalars } from '@generated/graphql'
import { cloudinary } from '../../../../config/cloudinary'

export type Props = {
  cloudinaryPublicId: string
  width: number | string
  height?: number | string
  transformation: Scalars['JSON']
  loop?: boolean
  autoPlay?: boolean
  muted?: boolean
  onMouseEnter?: (e: MouseEvent) => void
  onMouseLeave?: (e: MouseEvent) => void
  onEnded?: (val: boolean) => void
  showControls?: boolean
  poster?: string
}

const handleError = (error) => {
  // On occasion, this function is called with an argument that is not an error object.
  // I believe it's due to this component being rendered serverside, and the `onError`
  // event being called prematurely.
  if (error instanceof Error) {
    Sentry.captureException(error)
  }
}

const CloudinaryVideo = forwardRef<HTMLVideoElement, Props>(
  (
    {
      autoPlay,
      muted,
      cloudinaryPublicId,
      width,
      height,
      transformation,
      loop = false,
      onMouseEnter,
      onMouseLeave,
      onEnded,
      showControls = false,
      poster,
    },
    ref
  ) => {
    let baseTransformation = transformation
    if (!Array.isArray(baseTransformation)) {
      // fetch_format is unused - we need all transformation properties except that one.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fetch_format, ...restTransformations } = baseTransformation
      baseTransformation = restTransformations
    }

    const heightValue = typeof height === 'number' ? `${height}px` : height
    const widthValue = typeof width === 'number' ? `${width}px` : width

    useEffect(() => {
      const video = (ref as MutableRefObject<any>)?.current?.element?.current as HTMLVideoElement

      if (video && onMouseEnter) {
        video.addEventListener('mouseenter', onMouseEnter)
      }
      if (video && onMouseLeave) {
        video.addEventListener('mouseleave', onMouseLeave)
      }

      return () => {
        if (video && onMouseEnter) {
          video.removeEventListener('mouseenter', onMouseEnter)
        }
        if (video && onMouseLeave) {
          video.removeEventListener('mouseleave', onMouseLeave)
        }
      }
    }, [onMouseEnter, onMouseLeave, ref])

    return (
      <Video
        ref={ref}
        publicId={cloudinaryPublicId}
        cloud_name={process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME}
        poster={
          poster
            ? poster
            : cloudinary.video_thumbnail_url(cloudinaryPublicId, {
                transformation: baseTransformation,
                startOffset: 0,
                fetchFormat: 'jpg',
                secure: true,
              })
        }
        height={heightValue}
        width={widthValue}
        playsInline
        secure
        onEnded={() => {
          if (onEnded) {
            onEnded(false)
          }
        }}
        onError={handleError}
        sourceTypes={['mp4']}
        loop={loop}
        autoPlay={autoPlay}
        muted={muted}
        controls={showControls}
      >
        {Array.isArray(baseTransformation) &&
          transformation.map((chainedTransformation) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { fetch_format, ...restTransformations } = chainedTransformation

            return (
              <Transformation
                key={JSON.stringify(chainedTransformation)}
                {...restTransformations}
              />
            )
          })}
        {!Array.isArray(baseTransformation) && <Transformation {...baseTransformation} />}
      </Video>
    )
  }
)

export default memo(CloudinaryVideo)
